import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Chip, Stack,
  Typography, List, ListItem, ListItemText, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, Alert,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUploadField } from '../../components/FileUploadField';
import { omsApi, STATUS_ACTIONS, type Oms, type OmsAction } from '../../api/oms.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

interface Props {
  oms: Oms | null;
  onClose: () => void;
  onChanged: () => void;
}

const APPROVAL_ACTIONS: OmsAction[] = ['approve', 'reject', 'close', 'cancel'];

export function OmsDetailDialog({ oms, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = oms !== null;
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [docType, setDocType] = useState('approval_note');
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');

  const id = oms?.id ?? 0;
  const timeline = useQuery({ queryKey: ['oms-timeline', id], queryFn: () => omsApi.timeline(id), enabled: open });
  const links = useQuery({ queryKey: ['oms-links', id], queryFn: () => omsApi.listLinks(id), enabled: open });
  const docs = useQuery({ queryKey: ['oms-docs', id], queryFn: () => omsApi.listDocuments(id), enabled: open });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['oms-timeline', id] });
    qc.invalidateQueries({ queryKey: ['oms'] });
    onChanged();
  };

  const statusMut = useMutation({
    mutationFn: (action: OmsAction) => omsApi.changeStatus(id, action),
    onSuccess: refresh,
    onError: (e) => setError(extractApiError(e)),
  });
  const addDocMut = useMutation({
    mutationFn: () => omsApi.addDocument(id, { docType, fileName, fileUrl }),
    onSuccess: () => { setFileName(''); setFileUrl(''); setError(null); qc.invalidateQueries({ queryKey: ['oms-docs', id] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  if (!oms) return null;

  const availableActions = STATUS_ACTIONS[oms.status].filter((a) =>
    APPROVAL_ACTIONS.includes(a) ? hasPermission('oms.approve') : hasPermission('oms.update'),
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {oms.omsNumber} — {oms.title}
        <Chip size="small" label={oms.status} sx={{ ml: 1 }} />
        {oms.isLocked && <Chip size="small" color="warning" label="locked" sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {availableActions.length > 0 && (
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            {availableActions.map((a) => (
              <Button key={a} size="small" variant="outlined" onClick={() => statusMut.mutate(a)}>{a}</Button>
            ))}
          </Stack>
        )}
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab label="Timeline" /><Tab label={`Links (${links.data?.length ?? 0})`} /><Tab label="Documents" />
        </Tabs>

        {tab === 0 && (
          <List dense>
            {(timeline.data ?? []).map((e, i) => (
              <ListItem key={i} divider>
                <ListItemText
                  primary={`${e.type.toUpperCase()} · ${e.action}${e.to ? ` → ${e.to}` : ''}${e.txnRef ? ` · ${e.txnRef}` : ''}`}
                  secondary={new Date(e.at).toLocaleString() + (e.remarks ? ` · ${e.remarks}` : '')}
                />
              </ListItem>
            ))}
          </List>
        )}

        {tab === 1 && (
          <Table size="small">
            <TableHead><TableRow><TableCell>Type</TableCell><TableCell>Ref</TableCell><TableCell>Linked</TableCell></TableRow></TableHead>
            <TableBody>
              {(links.data ?? []).map((l) => (
                <TableRow key={l.id}><TableCell>{l.txnType}</TableCell><TableCell>{l.txnRef ?? '—'}</TableCell><TableCell>{new Date(l.createdAt).toLocaleString()}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {tab === 2 && (
          <Box>
            {hasPermission('oms.update') && (
              <Stack direction="row" spacing={1} mb={2}>
                <TextField select size="small" label="Type" value={docType} onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 160 }}>
                  {['approval_note', 'agreement', 'specification', 'other'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField size="small" label="File name" value={fileName} onChange={(e) => setFileName(e.target.value)} />
                <FileUploadField label="Document" value={fileUrl || null} onChange={(url) => setFileUrl(url ?? '')} />
                <Button variant="contained" disabled={!fileName || !fileUrl} onClick={() => addDocMut.mutate()}>Add</Button>
              </Stack>
            )}
            <Table size="small">
              <TableHead><TableRow><TableCell>Type</TableCell><TableCell>File</TableCell><TableCell>Version</TableCell><TableCell>Current</TableCell></TableRow></TableHead>
              <TableBody>
                {(docs.data ?? []).map((d) => (
                  <TableRow key={d.id}><TableCell>{d.docType}</TableCell><TableCell>{d.fileName}</TableCell><TableCell>v{d.version}</TableCell><TableCell>{d.isCurrent ? <Chip size="small" color="success" label="current" /> : '—'}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {!timeline.data && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
